import * as React from "react";
import { Box, FormControl, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid2";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { errorToast } from "../../../../utils/toast";
import TextInput from "../../../../components/TextInput";
import { CustomButton } from "../../../../components/Button";
import {
  useGenerateSummaryMutation,
  useHydrateThreadMutation,
  useSwitchLlmMutation,
} from "../../../../services/chat";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { tokensCost } from "../../../../utils/functions";
import { tokenApi } from "../../../../services/token";
import { ReactComponent as PencilIcon } from "../../../../assets/images/edit.svg";
import { IoSaveOutline } from "react-icons/io5";
import ReactMarkdown from "react-markdown";

type SummaryProps = {
  convoId: string | null;
  ai_llm: string | undefined;
  new_llm: string | undefined;
  confirmHydrateThread: (value?: boolean) => void;
  cancelHydrateThread: () => void;
  setShowConfirmation: (value: boolean) => void;
  setGeneratedSummary: (value: string) => void;
  generatedSummary: string;
  generatingSummary: boolean;
  hydrate: boolean;
  toast: any;
};

const ManageSummary: React.FC<SummaryProps> = ({
  convoId,
  ai_llm,
  new_llm,
  confirmHydrateThread,
  cancelHydrateThread,
  setShowConfirmation,
  setGeneratedSummary,
  generatedSummary,
  generatingSummary,
  hydrate,
  toast,
}) => {
  const [showHydrateThread, setShowHydrateThread] = React.useState(false);
  const [enableEditSummary, setEnableEditSummary] = React.useState(false);
  const [generateSummary, generateSummaryResult] = useGenerateSummaryMutation();
  const [regenerateMessage, setRegenerateMessage] = React.useState("");
  const [hydrateThread, hydrateThreadResult] = useHydrateThreadMutation();
  const [switchLlm, switchLlmResult] = useSwitchLlmMutation();
  const theme = useTheme();
  const metaData = useSelector((state: any) => state.metaData);
  const navigate = useNavigate();
  const user_id = metaData.userInfo["cognito:username"];
  const dispatch = useDispatch();
  const generateSummaryFunc = async () => {
    generatingSummary = true;
    const body = {
      conversationId: convoId,
      ai_llm: new_llm,
      message: regenerateMessage || "",
      user_id,
    };
    setEnableEditSummary(false);
    const response: any = await generateSummary(body);
    generatingSummary = false;
    setRegenerateMessage("");
    if (response.error) {
      toast.dismiss();
      errorToast(response.error.message || response.error.data.message);
      setShowConfirmation(false);
    } else {
      dispatch(tokenApi.util.invalidateTags(["tokenBalance"]));
      if (response.data && response.data.summary) {
        setGeneratedSummary(response.data.summary);
        setShowConfirmation(false);
        setShowHydrateThread(true);
      }
      if (response.data && !response.data.summary) {
        setShowConfirmation(false);
        errorToast("Could not generate summary. Thread health too low.");
      }
    }
  };

  React.useEffect(() => {
    if (!generatingSummary) {
      generateSummaryFunc();
    }
  }, []);

  const hydrateThreadFunc = async () => {
    const body = {
      old_conversation_id: convoId,
      user_id,
      ai_llm: !hydrate ? new_llm : ai_llm,
      context_summary: generatedSummary,
    };
    const response: any = !hydrate
      ? await switchLlm(body)
      : await hydrateThread(body);
    if (response.error) {
      errorToast(response.error.message || response.error.data.message);
    } else {
      dispatch(tokenApi.util.invalidateTags(["tokenBalance"]));
      if (response.data) {
        setShowHydrateThread(false);
        if (response.data.conversation_id) {
          navigate("/chat?conversation_id=" + response.data.conversation_id);
        }
        if (hydrate) {
          confirmHydrateThread(true);
        } else {
          confirmHydrateThread();
        }
      }
    }
  };

  return (
    <Dialog
      open={showHydrateThread}
      onClose={() => setShowHydrateThread(false)}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      sx={{
        "& .MuiDialog-paper": {
          backgroundColor: theme.palette.background.default,
          backgroundImage: "initial",
          textAlign: "center",
          width: { xs: "80%", sm: "45%" },
          boxShadow: "0 0 5px #222 !important",
          borderRadius: "10px",
        },
      }}
    >
      <DialogTitle onClick={() => setEnableEditSummary(false)}>
        Hydrate Thread
      </DialogTitle>
      <DialogContent sx={{ position: "relative" }}>
        <Grid container alignItems="center" justifyContent="center">
          <Grid size={{ xs: 12, md: 12 }} textAlign={"center"}>
            Summary
          </Grid>
          {generatedSummary && enableEditSummary ? (
            <IoSaveOutline
              style={{
                cursor: "pointer",
                position: "absolute",
                right: "20px",
                color: "#2a945f",
                fontSize: "25px",
              }}
              onClick={() => setEnableEditSummary(false)}
            />
          ) : generatedSummary &&
            !generateSummaryResult.isLoading &&
            !enableEditSummary ? (
            <PencilIcon
              style={{
                cursor: "pointer",
                position: "absolute",
                right: "20px",
              }}
              onClick={() => setEnableEditSummary(true)}
            />
          ) : null}
        </Grid>
        <Grid container alignItems={"center"} justifyContent={"center"}>
          <Grid size={{ xs: 12, md: 12 }}>
            {generatedSummary && enableEditSummary ? (
              <textarea
                className="summary"
                style={{
                  backgroundColor: theme.palette.background.default,
                  opacity: generateSummaryResult.isLoading ? "0.5" : "1",
                  overflow: "auto",
                  fontSize: "13px",
                  maxHeight: "320px",
                  minHeight: "320px",
                }}
                value={
                  generateSummaryResult.isLoading
                    ? "Generating summary ..."
                    : generatedSummary
                }
                onChange={(e) => setGeneratedSummary(e.target.value)}
              ></textarea>
            ) : generatedSummary && !enableEditSummary ? (
              <Box
                sx={{
                  borderRadius: "8px",
                  padding: "10px",
                  textAlign: "left",
                  fontSize: "13px",
                  maxHeight: "320px",
                  minHeight: "320px",
                  overflow: "auto",
                }}
              >
                <ReactMarkdown skipHtml={false}>
                  {generateSummaryResult.isLoading
                    ? "Generating summary ..."
                    : generatedSummary}
                </ReactMarkdown>
              </Box>
            ) : null}
          </Grid>
        </Grid>
        <Grid
          container
          alignItems="center"
          justifyContent="center"
          marginTop={2}
        >
          <Grid size={{ xs: 12, md: 12 }} textAlign={"left"}>
            Summary inputs
          </Grid>
        </Grid>
        <Grid
          container
          alignItems="center"
          justifyContent="center"
          spacing={2}
          marginTop={1}
        >
          <Grid size={{ xs: 8, md: 8 }} textAlign={"left"}>
            <FormControl variant="standard" fullWidth>
              <TextInput
                label="User input"
                placeholder="User input"
                ariaLabel="userinput"
                name="user-input"
                value={regenerateMessage}
                handleChange={(e) => setRegenerateMessage(e.target.value)}
              />
            </FormControl>
          </Grid>
          <Grid size={{ xs: 4, md: 4 }} textAlign={"left"}>
            <CustomButton
              text={"Regenerate summary"}
              disabled={generateSummaryResult.isLoading || !regenerateMessage}
              fontSize="12px"
              borderRadius="6px"
              height="40px"
              submit={generateSummaryFunc}
              endIcon={
                metaData.tokenMetadata.actionsTokensCost
                  ? tokensCost(
                      metaData.tokenMetadata.actionsTokensCost
                        .regenerate_summary,
                      { fontSize: "13px" }
                    )
                  : undefined
              }
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions
        sx={{
          padding: "10px !important",
          gap: { xs: "5px", sm: "0px" },
          justifyContent: "center",
        }}
      >
        <div style={{ minWidth: "fit-content" }}>
          <CustomButton
            text={"Cancel"}
            color="rgba(180, 69, 52, 1)"
            border="1px solid rgba(180, 69, 52, 1)"
            bgcolor={theme.palette.background.default}
            submit={() => {
              setShowHydrateThread(false);
              cancelHydrateThread();
            }}
            fontWeight="700"
            width="150px"
          />
        </div>
        <div>
          <CustomButton
            text={
              hydrateThreadResult.isLoading || switchLlmResult.isLoading
                ? ""
                : "Hydrate"
            }
            color={theme.palette.background.default}
            submit={hydrateThreadFunc}
            fontWeight="700"
            bgcolor="rgba(33, 143, 84, 1)"
            width="150px"
            disabled={generateSummaryResult.isLoading}
            endIcon={
              metaData.tokenMetadata.actionsTokensCost &&
              (!hydrateThreadResult.isLoading || !switchLlmResult.isLoading)
                ? tokensCost(
                    metaData.tokenMetadata.actionsTokensCost[
                      !hydrate ? "switch_llm" : "hydrate_thread"
                    ],
                    { fontSize: "13px" }
                  )
                : undefined
            }
          />
        </div>
      </DialogActions>
    </Dialog>
  );
};
export default ManageSummary;
